import { authSelectors } from '../../redux/modules/auth';
import { configSelectors } from '../../redux/modules/config';
import { generateLink, isCustomClientInUse } from './utils';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Lock, Share, Unlock } from '@lifeomic/chromicons';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { messages } from './intlLabels';
import { ShareLinkButtonProps, SelectOptionType } from './types';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useActiveAccountId } from '../../hooks/useActiveAccountId';
import { useActiveProjectId } from '../../hooks/useActiveProjectId';
import { useActiveProjectCohortId } from '../../hooks/useActiveProjectCohortId';
import { useIntl } from 'react-intl';
import { useNotify } from '../../hooks/useNotify';
import { useSelector } from 'react-redux';
import * as React from 'react';
import copy from 'copy-to-clipboard';

/**
 * @description Similar to ShareLinkButton, but should be used as a share link
 * button when inside of a modal.  The reason for this is that a modal overlaying
 * another modal isn't always great for UX, so we will use a button + menu
 * instead for that case.
 */
export const ModaledShareLinkButton: React.FC<ShareLinkButtonProps> = ({
  color = 'default',
  createLink,
  placement,
  ...rootProps
}) => {
  const { formatMessage } = useIntl();

  const activeProjectId = useActiveProjectId();
  const activeAccountId = useActiveAccountId();
  const activeCohortId = useActiveProjectCohortId();

  const auth = useSelector(authSelectors.selectAuthState);
  const config = useSelector(configSelectors.selectConfigState);

  const [notify] = useNotify();

  const handleClick = (type: SelectOptionType) => {
    const isUsingCustomClient = isCustomClientInUse(config, auth);
    if (isUsingCustomClient && type === 'open') {
      copy(
        createLink
          ? createLink({
              activeAccountId,
              activeProjectId,
              activeCohortId,
              isCustomClientInUse: isUsingCustomClient,
              type,
            })
          : generateLink({
              activeAccountId,
              activeProjectId,
              activeCohortId,
              url: window.location.href?.replace(`${activeAccountId}.`, ''),
            }),
      );
    } else {
      copy(
        createLink
          ? createLink({
              activeAccountId,
              activeProjectId,
              activeCohortId,
              isCustomClientInUse: isUsingCustomClient,
              type,
            })
          : generateLink({
              activeAccountId,
              activeProjectId,
              activeCohortId,
              url: window.location.href,
            }),
      );
    }

    notify(formatMessage(messages.notification), 'info');
  };

  if (!isCustomClientInUse(config, auth)) {
    return (
      <Tooltip placement="left" title={formatMessage(messages.tooltip)}>
        <IconButton
          aria-label={formatMessage(messages.tooltip)}
          color={color}
          size={0}
          icon={Share}
          onClick={() => handleClick('open')}
          {...rootProps}
        />
      </Tooltip>
    );
  }

  return (
    <Menu
      aria-label={formatMessage(messages.tooltip)}
      anchorElement={
        <span>
          <Tooltip placement="left" title={formatMessage(messages.tooltip)}>
            <IconButton
              aria-label={formatMessage(messages.tooltip)}
              color={color}
              size={0}
              icon={Share}
              {...rootProps}
            />
          </Tooltip>
        </span>
      }
      items={[
        <MenuItem
          key={'includeSSO' as SelectOptionType}
          text={formatMessage(messages.modalSelectIncludeSSO)}
          onClick={() => handleClick('includeSSO')}
          icon={Lock}
        />,
        <MenuItem
          key={'open' as SelectOptionType}
          text={formatMessage(messages.modalSelectMakeOpenLink)}
          onClick={() => handleClick('open')}
          icon={Unlock}
        />,
      ]}
      placement={placement}
    />
  );
};
